import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './EmailVerification.module.css';
import CustomButton from '../../components/buttons/CustomButton';
import InputField from '../../components/inputfield/InputField';
import LogoImage from '../../assets/images/logo-full.png';
import Loading from '../../components/loading/Loading';
import SuccessMessage from '../../components/success-message/SuccessMessage';
import { ConfirmEmailAPI, ResendVerificationEmailAPI } from '../../api/API';
import { clearAccessToken } from '../../api/authTokens';
import {
  trackVerificationCompleted,
  trackVerificationFailed,
  trackFirstLoginAfterVerification,
  trackVerificationResent,
  trackGoogleAdsConfirmedSignupConversion,
  afterConfirmedSignupConversion
} from '../../utils/analytics';

// Mensagens por código de erro retornado pelo backend (auth/email_verification_confirm.php)
const ERROR_MESSAGES = {
  VERIFICATION_TOKEN_EXPIRED: 'Este link de confirmação expirou.',
  VERIFICATION_TOKEN_USED: 'Este link já foi utilizado. Faça login.',
  VERIFICATION_TOKEN_INVALID: 'Link inválido. Verifique se copiou o endereço completo do e-mail.'
};

function ConfirmEmail({ onSuccess }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [errorCode, setErrorCode] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  // Dados de sessão devolvidos por ConfirmEmailAPI, guardados em vez de repassados
  // imediatamente a onSuccess. Ver handleActivateSession: só ativamos a sessão no
  // App (localStorage/token/estado global) no instante do redirecionamento, para
  // que a checagem de assinatura/setup do App (useEffect em [session] em App.js,
  // que pode disparar window.location.replace para /setup-wizard ou
  // /subscription/plans) não derrube esta tela antes do usuário conseguir ler a
  // mensagem de sucesso.
  const [loginPayload, setLoginPayload] = useState(null);
  const sessionActivatedRef = useRef(false);

  const [email, setEmail] = useState(() => {
    try {
      return sessionStorage.getItem('pendingVerificationEmail') || '';
    } catch (e) {
      return '';
    }
  });
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);
  const [resendError, setResendError] = useState(null);

  const emailRef = useRef(null);
  // Evita disparar ConfirmEmailAPI duas vezes: o token é de uso único, então uma
  // segunda chamada (ex.: double-invoke de efeitos em React.StrictMode/dev)
  // retornaria VERIFICATION_TOKEN_USED e sobrescreveria o estado de sucesso.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    // Limpa sessão/local storage residual antes de processar o token,
    // mesmo padrão usado em ResetPassword.js para evitar estado inválido.
    clearAccessToken();
    localStorage.removeItem('userData');
    localStorage.removeItem('sessionData');

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (!token) {
      setStatus('error');
      setErrorCode(null);
      setErrorMessage('Link de confirmação inválido. Verifique se copiou o endereço completo do e-mail.');
      return;
    }

    ConfirmEmailAPI(token, (response) => {
      if (response && response.error) {
        const code = response.code || null;
        setStatus('error');
        setErrorCode(code);
        setErrorMessage(ERROR_MESSAGES[code] || response.error || 'Não foi possível confirmar seu e-mail.');
        trackVerificationFailed({ reason: code || 'unknown' });
        return;
      }

      setStatus('success');
      trackVerificationCompleted();
      trackFirstLoginAfterVerification();
      // Conversão do Google Ads (mesmo id da tela "Confirme seu e-mail": conta uma vez)
      trackGoogleAdsConfirmedSignupConversion(response.conversion_id);

      try {
        sessionStorage.removeItem('pendingVerificationEmail');
      } catch (e) {
        // sessionStorage indisponível - não bloqueia o fluxo
      }

      // Não chama onSuccess aqui: a sessão só é ativada em handleActivateSession,
      // disparado pelo SuccessMessage no momento do redirecionamento.
      // O refresh token não vem no JSON: o servidor o gravou no cookie HttpOnly
      setLoginPayload({
        token: response.token,
        session: response.session
      });
    });
    // Executa apenas uma vez, ao montar o componente (leitura do token da URL)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ativa a sessão no App (grava token/session) só quando o usuário
  // de fato sai desta tela — clique em "Entrar agora" ou fim da contagem regressiva.
  // A ref evita ativar duas vezes (ex.: StrictMode ou corrida entre clique e timer).
  const handleActivateSession = () => {
    if (sessionActivatedRef.current) return;
    sessionActivatedRef.current = true;

    if (onSuccess && loginPayload) {
      // A entrada recarrega a página: espera a conversão do Google Ads sair antes
      afterConfirmedSignupConversion(() => onSuccess(loginPayload));
    }
  };

  const doResend = (targetEmail) => {
    if (!targetEmail || resendLoading) return;

    setResendLoading(true);
    setResendMessage(null);
    setResendError(null);

    ResendVerificationEmailAPI(targetEmail, (response) => {
      setResendLoading(false);

      if (response && response.error) {
        setResendError(response.error);
        return;
      }

      trackVerificationResent();
      setResendMessage(
        (response && response.message) ||
          'Enviamos um novo link de confirmação. Verifique sua caixa de entrada.'
      );
    });
  };

  const handleResendClick = () => {
    if (email) {
      doResend(email);
    } else {
      setShowEmailPrompt(true);
    }
  };

  const handleResendWithTypedEmail = () => {
    const typedEmail = emailRef.current ? emailRef.current.GetValue() : '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!typedEmail || !emailRegex.test(typedEmail)) {
      if (emailRef.current) emailRef.current.SetError('Digite um e-mail válido');
      return;
    }

    setEmail(typedEmail);
    doResend(typedEmail);
  };

  if (status === 'loading') {
    return (
      <div className={styles.container}>
        <Loading />
      </div>
    );
  }

  if (status === 'success') {
    return (
      <SuccessMessage
        title="E-mail confirmado!"
        message="Sua conta foi ativada com sucesso. Vamos te levar para o painel."
        redirectPath="/dashboard"
        redirectLabel="Entrar agora"
        autoRedirectDelay={6}
        onBeforeRedirect={handleActivateSession}
        fullPage
        logoSrc={LogoImage}
      />
    );
  }

  const showResendOption = errorCode !== 'VERIFICATION_TOKEN_USED';

  return (
    <div className={styles.container}>
      <div className={styles.containerForm}>
        <div className={styles.logoContainer}>
          <img className={styles.logo} src={LogoImage} alt="Logo" />
          <h1 className={styles.title}>Não foi possível confirmar</h1>
        </div>

        <div className={styles.errorMessage}>{errorMessage}</div>

        {showResendOption && (
          <>
            {resendMessage && <div className={styles.successMessage}>{resendMessage}</div>}
            {resendError && <div className={styles.errorMessage}>{resendError}</div>}

            {!showEmailPrompt && (
              <CustomButton
                variant="contained"
                style="primary"
                label="Reenviar e-mail de confirmação"
                onClick={handleResendClick}
                enabled={!resendLoading}
                loading={resendLoading}
              />
            )}

            {showEmailPrompt && (
              <div className={styles.changeEmailForm}>
                <InputField
                  Width={'100%'}
                  Label={'Seu e-mail'}
                  type="email"
                  placeholder="Digite o e-mail usado no cadastro"
                  ref={emailRef}
                />
                <CustomButton
                  variant="contained"
                  style="primary"
                  label="Reenviar e-mail de confirmação"
                  onClick={handleResendWithTypedEmail}
                  enabled={!resendLoading}
                  loading={resendLoading}
                />
              </div>
            )}
          </>
        )}

        <div className={styles.backLink}>
          <Link to="/login">Voltar para o login</Link>
        </div>
      </div>
    </div>
  );
}

export default ConfirmEmail;
